// Performance optimization utilities

/**
 * Debounce function to limit how often a function can be called
 * @param {Function} func - The function to debounce
 * @param {number} wait - The delay in milliseconds
 * @returns {Function} - The debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function to limit function calls to once per specified time period
 * @param {Function} func - The function to throttle
 * @param {number} limit - The time limit in milliseconds
 * @returns {Function} - The throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Lazy load images using Intersection Observer
 * @param {string} selector - CSS selector for images to lazy load
 */
export const lazyLoadImages = (selector = 'img[data-src]') => {
  const images = document.querySelectorAll(selector);
  
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          img.classList.add('lazy-loaded');
          if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
          }
          observer.unobserve(img);
        }
      });
    }, {
      rootMargin: '50px 0px',
      threshold: 0.01
    });

    images.forEach(img => imageObserver.observe(img));
  } else {
    // Fallback for browsers that don't support Intersection Observer
    images.forEach(img => {
      img.src = img.dataset.src;
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
      }
    });
  }
};

/**
 * Preload critical resources
 * @param {Array} resources - Array of resource URLs to preload
 */
export const preloadResources = (resources) => {
  resources.forEach(resource => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = resource.url;
    link.as = resource.type || 'fetch';
    if (resource.crossOrigin) {
      link.crossOrigin = resource.crossOrigin;
    }
    document.head.appendChild(link);
  });
};

/**
 * Request idle callback polyfill
 */
export const requestIdleCallback = window.requestIdleCallback || 
  function (cb) {
    const start = Date.now();
    return setTimeout(function () {
      cb({
        didTimeout: false,
        timeRemaining: function () {
          return Math.max(0, 50 - (Date.now() - start));
        }
      });
    }, 1);
  };

/**
 * Cancel idle callback polyfill
 */
export const cancelIdleCallback = window.cancelIdleCallback || 
  function (id) {
    clearTimeout(id);
  };

/**
 * Optimize scroll performance with passive listeners
 */
export const optimizeScrollPerformance = () => {
  // Check if passive listeners are supported
  let supportsPassive = false;
  try {
    const opts = Object.defineProperty({}, 'passive', {
      get: function() {
        supportsPassive = true;
      }
    });
    window.addEventListener('testPassive', null, opts);
    window.removeEventListener('testPassive', null, opts);
  } catch (e) {}

  return supportsPassive ? { passive: true } : false;
};

/**
 * Memoize expensive function calls
 * @param {Function} fn - Function to memoize
 * @returns {Function} - Memoized function
 */
export const memoize = (fn) => {
  const cache = new Map();
  return (...args) => {
    const key = JSON.stringify(args);
    if (cache.has(key)) {
      return cache.get(key);
    }
    const result = fn(...args);
    cache.set(key, result);
    return result;
  };
};

/**
 * Virtual scroll helper for large lists
 * @param {Object} config - Configuration object
 * @returns {Object} - Virtual scroll instance
 */
export const createVirtualScroll = (config) => {
  const {
    container,
    itemHeight,
    totalItems,
    renderItem,
    buffer = 5
  } = config;

  let scrollTop = 0;
  let visibleStart = 0;
  let visibleEnd = 0;

  const update = () => {
    scrollTop = container.scrollTop;
    const containerHeight = container.clientHeight;
    
    visibleStart = Math.floor(scrollTop / itemHeight) - buffer;
    visibleEnd = Math.ceil((scrollTop + containerHeight) / itemHeight) + buffer;
    
    visibleStart = Math.max(0, visibleStart);
    visibleEnd = Math.min(totalItems - 1, visibleEnd);
    
    render();
  };

  const render = () => {
    const fragment = document.createDocumentFragment();
    
    for (let i = visibleStart; i <= visibleEnd; i++) {
      const item = renderItem(i);
      item.style.position = 'absolute';
      item.style.top = `${i * itemHeight}px`;
      fragment.appendChild(item);
    }
    
    container.innerHTML = '';
    container.appendChild(fragment);
    container.style.height = `${totalItems * itemHeight}px`;
  };

  container.addEventListener('scroll', throttle(update, 16));
  update();

  return {
    update,
    destroy: () => {
      container.removeEventListener('scroll', update);
    }
  };
}; 